import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { useTranslation } from 'react-i18next'
import { normalizedWarrantName } from '@finance-lab/utils/shared'
import { WatchListEntity, WatchListItem, WatchListItemType } from '@finance-lab/utils/types'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import UserEventApi from '../../api/UserEventApi'
import { WatchListsApi } from '../../api/WatchListApi'
import { WatchListGroup } from './WatchListGroup'
import { WatchListButton } from '../components/WatchListButton'

const watchListApi = new WatchListsApi()

const groupWatchListItems = (watchList: WatchListEntity | null) =>
    (watchList?.items || []).reduce(
        (acc, item) => {
            const type: WatchListItemType | null =
                item.issueId !== null
                    ? 'Issue'
                    : item.warrantId !== null
                      ? 'Warrant'
                      : item.companyId !== null
                        ? 'Company'
                        : item.investorId !== null
                          ? 'Investor'
                          : item.convertibleId !== null
                            ? 'Convertible'
                            : null

            if (type === null) {
                console.error(`Unknown watch item type: `, item)
                return acc
            }

            const name = type === 'Warrant' ? normalizedWarrantName(item) : item.name

            return {
                ...acc,
                [type]: [...(acc[type] || []), { ...item, name: name || '-' }].sort((a, b) =>
                    (a.name as string).localeCompare(b.name as string),
                ),
            }
        },
        { Company: [], Investor: [], Issue: [], Warrant: [], Convertible: [] } as Record<
            WatchListItemType,
            WatchListItem[]
        >,
    )

export const WatchListView = () => {
    const { t } = useTranslation()
    const [watchList, setWatchList] = useState<WatchListEntity | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        void (async () => {
            const [result] = await watchListApi.getAll()

            setWatchList(result)
            setIsLoading(false)
        })()
    }, [])

    useLogUserEvent(UserEventApi.EVENT_TYPES.SHOW_WATCH_LIST)

    const groupedItems = groupWatchListItems(watchList)
    const groups = Object.keys(groupedItems) as WatchListItemType[]

    const handleChange = async () => {
        const [result] = await watchListApi.getAll()

        setWatchList(result)
    }

    return (
        <>
            <Helmet title={t('pages.watchLists.title')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                {t('pages.watchLists.title')}
                <WatchListButton disabled isInWatchList />
            </Typography>
            <Divider sx={{ my: 6 }} />
            <div style={{ maxWidth: 1200 }}>
                {groups &&
                    groupedItems &&
                    groups.map((key) => (
                        <WatchListGroup
                            key={key}
                            isLoading={isLoading}
                            items={groupedItems[key]}
                            onChange={handleChange}
                            type={key}
                        />
                    ))}
            </div>
        </>
    )
}
